import React, { createContext, useContext, useEffect, useMemo, useState } from 'react'

type Theme = 'light' | 'dark' | 'system'

type ThemeContextValue = {
  theme: Theme
  setTheme: (t: Theme) => void
  isDark: boolean
  reducedMotion: boolean
  setReducedMotion: (v: boolean) => void
}

const ThemeContext = createContext<ThemeContextValue | undefined>(undefined)

function getSystemDark(): boolean {
  return typeof window !== 'undefined' && window.matchMedia && window.matchMedia('(prefers-color-scheme: dark)').matches
}

export const ThemeProvider: React.FC<React.PropsWithChildren> = ({ children }) => {
  const [theme, setTheme] = useState<Theme>(() => (localStorage.getItem('veridoc:theme') as Theme) || 'system')
  const [reducedMotion, setReducedMotion] = useState<boolean>(() => localStorage.getItem('veridoc:reducedMotion') === '1')
  const isDark = theme === 'dark' || (theme === 'system' && getSystemDark())

  useEffect(() => {
    localStorage.setItem('veridoc:theme', theme)
    const root = document.documentElement
    if (isDark) root.classList.add('dark'); else root.classList.remove('dark')
  }, [theme, isDark])

  useEffect(() => {
    localStorage.setItem('veridoc:reducedMotion', reducedMotion ? '1' : '0')
  }, [reducedMotion])

  const value = useMemo(() => ({ theme, setTheme, isDark, reducedMotion, setReducedMotion }), [theme, isDark, reducedMotion])
  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>
}

export function useTheme() {
  const ctx = useContext(ThemeContext)
  if (!ctx) throw new Error('useTheme must be used within ThemeProvider')
  return ctx
}
